import React, {useEffect, useMemo, useRef, useState} from 'react';
import { useHistory } from 'react-router-dom'
import "../scss/SupportBots.scss"
import { useIntl } from "react-intl";
import {
    Bot,
    Plus,
    PanelTopClose,
    PanelTopOpen,
} from 'lucide-react';
import {Link} from "react-router-dom";
import {useSupportBotContext, useSupportBotData} from "./SupportBotEditor/SupportBotData";
import {useCreateBotVersion} from "./SupportBotEditor/hooks/useCreateBotVersion";
import {BotCard} from "../components/BotCard/BotCard";
import {updateBotById} from "../api/APIBot";
import {Modal} from "../UIElements";
import {queryClient} from "../App";
import {useSnackbar} from "../Content";
import ModuleGeneratorModal from "./SupportBotEditor/components/ModuleGeneratorModal";

export default function SupportBots() {
    const {
        isLoading,
        isError,
        isSuccess,
        aiProvider,
        data: {
            supportBots,
            supportTopics,
            geminiConfig,
        } = {} // Provide default empty object to avoid destructuring errors
    } = useSupportBotData();

    const isGemini = aiProvider === "gemini";
    const hasModuleGenerator = isGemini && !!geminiConfig?.moduleGenerator;

    const [searchValue, setSearchValue] = useState("")
    const [allExpanded, setAllExpanded] = useState(false)
    const [showGenerator, setShowGenerator] = useState(false)
    const intl = useIntl()

    useEffect(() => {
        document.title = intl.formatMessage({
            id: "supportBots",
            defaultMessage: "Support Bots",
        }) + " | Annie Admin"
    }, [intl])

    const handleSearchChange = (e) => {
        const value = e.target.value
        setSearchValue(value)
    }

    return (
        <div className="content">
            <div className="header-container">
                <h1>
                    <Bot size={24} strokeWidth={2} />
                    {intl.formatMessage({
                        id: 'supportBots',
                        defaultMessage: 'Support Bots'
                    })}
                </h1>
            </div>
            <div className={"toolbar support-bots-toolbar"}>
                <input type={"search"}
                       value={searchValue}
                       onChange={handleSearchChange}
                       placeholder={intl.formatMessage({
                           defaultMessage: 'Search bots',
                           id: 'searchBots'
                       })}
                />
                {hasModuleGenerator ? (
                    <button onClick={() => setShowGenerator(true)} className={"create-new button"}>
                        <Plus size={16} strokeWidth={3} />
                        {intl.formatMessage({
                            id: 'createNewSupportBot',
                            defaultMessage: 'New Support Bot',
                        })}
                    </button>
                ) : (
                    <Link to={`/support-bot/new`} className={"create-new button"}>
                        <Plus size={16} strokeWidth={3} />
                        {intl.formatMessage({
                            id: 'createNewSupportBot',
                            defaultMessage: 'New Support Bot',
                        })}
                    </Link>
                )}
                <button className={"icon-button toggle"}
                        onClick={() => setAllExpanded(!allExpanded)}
                        title={allExpanded ? intl.formatMessage({
                            id: 'collapseAll',
                            defaultMessage: 'Collapse All'
                        }) : intl.formatMessage({
                            id: 'expandAll',
                            defaultMessage: 'Expand All'
                        })}
                >
                    {allExpanded ?
                        <PanelTopClose /> :
                        <PanelTopOpen />
                    }
                </button>
            </div>

            {isLoading && (
                <div className="loading-container">
                    <p>{intl.formatMessage({
                        id: 'loadingBots',
                        defaultMessage: 'Loading support bots...'
                    })}</p>
                </div>
            )}

            {isError && (
                <div className="error-container">
                    <p>{intl.formatMessage({
                        id: 'errorLoadingBots',
                        defaultMessage: 'Error loading support bots. Please try again later.'
                    })}</p>
                    <button
                        className="retry-button"
                        onClick={() => window.location.reload()}
                    >
                        {intl.formatMessage({
                            id: 'retry',
                            defaultMessage: 'Retry'
                        })}
                    </button>
                </div>
            )}

            {isSuccess && (
                <SupportBotsContent
                    supportBots={supportBots}
                    searchValue={searchValue}
                    allExpanded={allExpanded}
                />
            )}

            {showGenerator && (
                <ModuleGeneratorModal
                    geminiConfig={geminiConfig}
                    onClose={() => setShowGenerator(false)}
                />
            )}
        </div>
    )
}

// Separated component that handles the supportBots data
function SupportBotsContent({ supportBots, searchValue, allExpanded }) {
    const [versionFilters, setVersionFilters] = useState({})
    const [expandedGroups, setExpandedGroups] = useState({})
    const [deleteTarget, setDeleteTarget] = useState(null)
    const [isDeleting, setIsDeleting] = useState(false)
    const intl = useIntl()
    const { setSnackbarData } = useSnackbar()

    // Initialize version filters with DEFAULT version for each group
    useEffect(() => {
        const initialVersions = {};
        const botsArray = Object.values(supportBots);

        botsArray.forEach(bot => {
            const versionGroup = bot.config?.versionGroup;
            const version = bot.config?.version;

            if (versionGroup) {
                if (!initialVersions[versionGroup]) {
                    initialVersions[versionGroup] = version;
                }
                if (version === "DEFAULT") {
                    initialVersions[versionGroup] = "DEFAULT";
                }
            }
        });

        setVersionFilters(initialVersions);
    }, [supportBots]);


    const botGroups = useMemo(() => {
        const groups = {};

        Object.entries(supportBots).forEach(([key, bot]) => {
            const groupKey = bot.config?.versionGroup || key;

            if (!groups[groupKey]) {
                groups[groupKey] = {
                    bots: [],
                    latestUpdate: null,
                    groupKey: groupKey
                };
            }

            groups[groupKey].bots.push({ key, bot });

            const botDate = new Date(bot.updated);
            if (!groups[groupKey].latestUpdate || botDate > groups[groupKey].latestUpdate) {
                groups[groupKey].latestUpdate = botDate;
            }
        });

        // Sort groups by latest update
        return Object.values(groups).sort((a, b) => b.latestUpdate - a.latestUpdate);
    }, [supportBots]);

    // Filter bots based on search and version filters
    const filteredBotData = botGroups
        .map(group => {
            const botToShow = group.bots.find(({ bot }) => {
                if (bot.config?.versionGroup) {
                    return versionFilters[bot.config.versionGroup] === bot.config.version;
                }
                return true;
            });

            return botToShow;
        })
        .filter(item => {
            if (!item) return false;

            const { bot } = item;
            return bot?.config?.name &&
                bot.config.name.toLowerCase().includes(searchValue.toLowerCase());
        });

    const handleVersionChange = (versionGroup, version) => {
        setVersionFilters(prev => ({
            ...prev,
            [versionGroup]: version
        }));
    }

    const toggleExpanded = (botId, versionGroup, isExpanded) => {
        const toggleId = versionGroup || botId;
        setExpandedGroups(prev => ({
            ...prev,
            [toggleId]: isExpanded
        }));
    }

    const handleDeleteRequest = (botId, versionGroup, version) => {
        const bot = Object.values(supportBots).find(b => b.id === botId);
        if (!bot) return;

        const isDefault = version === "DEFAULT";
        const allVersionBots = isDefault && versionGroup
            ? Object.values(supportBots).filter(b => b.config?.versionGroup === versionGroup)
            : [bot];

        setDeleteTarget({
            botId,
            botName: bot.config?.name || `Bot #${botId}`,
            version,
            versionGroup,
            isDefault,
            isActive: allVersionBots.some(b => b.status === "ACTIVE"),
            botsToDelete: allVersionBots,
            allVersionNames: allVersionBots.map(b => b.config?.version).filter(Boolean),
        });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            for (const bot of deleteTarget.botsToDelete) {
                await updateBotById(bot.id, { ...bot, status: "DELETED" });
            }
            queryClient.invalidateQueries('bots');
            setSnackbarData({
                show: true,
                text: intl.formatMessage({ id: 'bot.deleteSuccess', defaultMessage: 'Bot module deleted.' }),
                state: 'success',
                autoHide: true,
            });
            setDeleteTarget(null);
        } catch (err) {
            console.error('Error deleting bot module:', err);
            setSnackbarData({
                show: true,
                text: intl.formatMessage({ id: 'bot.deleteError', defaultMessage: 'Failed to delete bot module. Please try again.' }),
                state: 'error',
                autoHide: true,
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const getDeleteModalText = () => {
        if (!deleteTarget) return '';
        const { isDefault, botName, version, allVersionNames, isActive } = deleteTarget;

        let text = '';
        if (isDefault && allVersionNames.length > 1) {
            const otherVersions = allVersionNames.filter(v => v !== "DEFAULT").join(', ');
            text = intl.formatMessage(
                { id: 'bot.deleteAllVersions', defaultMessage: 'Deleting the default version will delete {botName} and all its versions ({versions}).' },
                { botName, versions: otherVersions }
            );
        } else if (isDefault) {
            text = intl.formatMessage(
                { id: 'bot.deleteModule', defaultMessage: 'Are you sure you want to delete {botName}?' },
                { botName }
            );
        } else {
            text = intl.formatMessage(
                { id: 'bot.deleteVersion', defaultMessage: 'Are you sure you want to delete version {version} of {botName}?' },
                { version, botName }
            );
        }

        if (isActive) {
            text += ' ' + intl.formatMessage({ id: 'bot.deleteActiveWarning', defaultMessage: 'This bot is currently active and may be in use.' });
        }

        return text;
    };

    return (
        <div className="support-bots">
            {filteredBotData.length === 0 ? (
                <div className="no-results">
                    <p>{intl.formatMessage({
                        id: 'noBotsFound',
                        defaultMessage: 'No support bots match your search.'
                    })}</p>
                </div>
            ) : (
                filteredBotData.map(({ key, bot } ) => {
                    // Determine if this bot/group should be expanded
                    const groupId = bot.config?.versionGroup || bot?.id
                    const isExpanded = allExpanded || expandedGroups[groupId] || false

                    return <BotBlockWrapper
                        bot={bot}
                        botId={bot?.id}
                        key={key}
                        isExpanded={isExpanded}
                        onVersionChange={handleVersionChange}
                        onToggleExpanded={toggleExpanded}
                        onDelete={handleDeleteRequest}
                    />
                })
            )}

            {deleteTarget && (
                <Modal
                    header={intl.formatMessage({ id: 'bot.deleteTitle', defaultMessage: 'Delete bot module' })}
                    text={getDeleteModalText()}
                    closeModal={() => setDeleteTarget(null)}
                    confirmAction={handleDeleteConfirm}
                    confirmDisabled={isDeleting}
                    confirmText={isDeleting
                        ? intl.formatMessage({ id: 'deleting', defaultMessage: 'Deleting...' })
                        : intl.formatMessage({ id: 'survey.delete', defaultMessage: 'Delete' })
                    }
                    alert={true}
                />
            )}
        </div>
    );
}

function BotBlockWrapper({ bot, botId, isExpanded, onVersionChange, onToggleExpanded, onDelete }) {
    const history = useHistory();
    const { data: { users, supportTopicCodes }, aiProvider } = useSupportBotContext();
    const { createNewVersion } = useCreateBotVersion();

    return (
        <BotCard
            data={bot}
            botId={botId}
            mode="view"
            isGemini={aiProvider === "gemini"}
            isExpanded={isExpanded}
            onHeaderClick={(id) => history.push(`/support-bot/${id}`)}
            onToggleExpanded={onToggleExpanded}
            onVersionChange={onVersionChange}
            onCreateVersion={createNewVersion}
            onDelete={onDelete}
            userDataSource={users}
            topicDataSource={supportTopicCodes}
        />
    );
}